import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Card, Table, Button, Modal, Form, Alert,
  Badge, Row, Col, InputGroup
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, faEdit, faTrashAlt, faTimes, faSave, faPalette, faChartLine
} from '@fortawesome/free-solid-svg-icons';
import { iconOptions, getIconFromString } from '../../utils/iconUtils';
import { api } from '../../config/api';

const CategoryList = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: 'tag',
    color: '#6c757d'
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Icon options are now imported from iconUtils.js
  
  // Predefined colors
  const colorOptions = [
    '#4CAF50', '#2196F3', '#9C27B0', '#FF5722', '#FF9800',
    '#E91E63', '#00BCD4', '#795548', '#607D8B', '#3F51B5',
    '#F44336', '#009688', '#FFEB3B', '#8BC34A', '#673AB7'
  ];
  
  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const response = await api.get('/categories');
        setCategories(response.data.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setError('Failed to load categories. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchCategories();
  }, []);
  
  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  // Open modal for creating a new category
  const handleOpenCreateModal = () => {
    setEditCategory(null);
    setFormData({
      name: '',
      description: '',
      icon: 'tag', // Default icon name
      color: '#6c757d'
    });
    setFormErrors({});
    setShowModal(true);
  };
  
  // Open modal for editing a category
  const handleOpenEditModal = (category) => {
    setEditCategory(category);
    setFormData({
      name: category.name,
      description: category.description || '',
      icon: category.icon || 'tag',
      color: category.color || '#6c757d'
    });
    setFormErrors({});
    setShowModal(true);
  };
  
  // Close modal
  const handleCloseModal = () => {
    setShowModal(false);
  };
  
  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (formData.name.length > 50) {
      errors.name = 'Name cannot exceed 50 characters';
    }
    
    if (formData.description.length > 200) {
      errors.description = 'Description cannot exceed 200 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Save category (create or update)
  const handleSaveCategory = async () => {
    if (!validateForm()) return;
    
    try {
      let response;
      
      if (editCategory) {
        // Update existing category
        response = await api.put(`/categories/${editCategory.id}`, formData);
        
        // Update categories state
        if (response.data.success) {
          setCategories(prevCategories => 
            prevCategories.map(cat => 
              cat.id === editCategory.id ? response.data.data : cat
            )
          );
        }
      } else {
        // Create new category
        response = await api.post('/categories', formData);
        
        // Update categories state
        if (response.data.success) {
          setCategories(prevCategories => [...prevCategories, response.data.data]);
        }
      }
      
      // Close modal on success
      if (response.data.success) {
        setShowModal(false);
      } else {
        setFormErrors({ general: response.data.message || 'Failed to save category' });
      }
    } catch (err) {
      console.error('Error saving category:', err);
      setFormErrors({ 
        general: err.response?.data?.message || 'An error occurred while saving the category' 
      });
    }
  };
  
  // Delete category
  const handleDeleteCategory = async (categoryId) => {
    // Don't allow deletion of default categories
    const category = categories.find(cat => cat.id === categoryId);
    if (category.isDefault) {
      alert('Default categories cannot be deleted.');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    
    try {
      const response = await api.delete(`/categories/${categoryId}`);
      
      if (response.data.success) {
        // Remove from state
        setCategories(prevCategories => 
          prevCategories.filter(cat => cat.id !== categoryId)
        );
      } else {
        alert(response.data.message || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      alert('An error occurred while deleting the category');
    }
  };

  if (loading && categories.length === 0) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading categories...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="my-3">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <div className="categories-list">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h1 className="mb-0">Categories</h1>
        <div className="d-flex gap-2">
          <Button as={Link} to="/analysis" variant="outline-primary">
            <FontAwesomeIcon icon={faChartLine} className="me-2" />
            Analyse spend
          </Button>
          <Button 
            variant="primary" 
            onClick={handleOpenCreateModal}
          >
            <FontAwesomeIcon icon={faPlus} className="me-2" />
            Add Category
          </Button>
        </div>
      </div>
      
      <Card className="shadow-sm mb-4">
        <Table responsive hover className="mb-0">
          <thead>
            <tr>
              <th>Color</th>
              <th>Icon</th>
              <th>Name</th>
              <th>Description</th>
              <th className="text-center">Type</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-4">
                  No categories found. Create your first category to get started!
                </td>
              </tr>
            ) : (
              categories.map(category => (
                <tr key={category.id}>
                  <td>
                    <div 
                      className="category-color-dot"
                      style={{ 
                        backgroundColor: category.color || '#6c757d',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%'
                      }}
                    ></div>
                  </td>
                  <td>
                    <div 
                      className="category-icon"
                      style={{ 
                        color: category.color || '#6c757d',
                        width: '24px',
                        textAlign: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={getIconFromString(category.icon)} />
                    </div>
                  </td>
                  <td>
                    <Link to={`/analysis/categories/${category.id}`}>
                      {category.name}
                    </Link>
                  </td>
                  <td className="text-muted">{category.description}</td>
                  <td className="text-center">
                    {category.isDefault ? (
                      <Badge bg="info">Default</Badge>
                    ) : (
                      <Badge bg="secondary">Custom</Badge>
                    )}
                  </td>
                  <td className="text-end">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => handleOpenEditModal(category)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={category.isDefault}
                      onClick={() => handleDeleteCategory(category.id)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Card>
      
      {/* Category Edit/Create Modal */}
      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {editCategory ? 'Edit Category' : 'Add Category'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {formErrors.general && (
            <Alert variant="danger" className="mb-3">
              {formErrors.general}
            </Alert>
          )}
          
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control 
                type="text" 
                name="name" 
                value={formData.name}
                onChange={handleInputChange}
                isInvalid={!!formErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control 
                as="textarea" 
                rows={2} 
                name="description" 
                value={formData.description}
                onChange={handleInputChange}
                isInvalid={!!formErrors.description}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.description}
              </Form.Control.Feedback>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Icon</Form.Label>
              <div className="border rounded p-2">
                <Row className="g-2">
                  {iconOptions.map(iconOption => (
                    <Col key={iconOption.name} xs={3}>
                      <Button
                        variant={formData.icon === iconOption.name ? 'primary' : 'light'}
                        className="w-100 py-2"
                        onClick={() => setFormData({ ...formData, icon: iconOption.name })}
                      >
                        <FontAwesomeIcon icon={iconOption.icon} />
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Color</Form.Label>
              <div className="mb-3">
                <Row className="g-2">
                  {colorOptions.map(color => (
                    <Col key={color} xs={3}>
                      <Button
                        style={{ 
                          backgroundColor: color, 
                          borderColor: formData.color === color ? '#000' : color 
                        }}
                        className="w-100 p-3"
                        onClick={() => setFormData({ ...formData, color })}
                      >
                        {formData.color === color && (
                          <FontAwesomeIcon icon="check" color="#fff" />
                        )}
                      </Button>
                    </Col>
                  ))}
                </Row>
              </div>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faPalette} />
                </InputGroup.Text>
                <Form.Control 
                  type="color" 
                  name="color" 
                  value={formData.color}
                  onChange={handleInputChange}
                />
                <Form.Control 
                  type="text" 
                  name="color" 
                  value={formData.color}
                  onChange={handleInputChange}
                />
              </InputGroup>
            </Form.Group>

            <div className="mt-4">
              <h6>Preview:</h6>
              <div className="p-3 border rounded d-flex align-items-center">
                <div 
                  style={{ 
                    backgroundColor: formData.color,
                    color: '#fff',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '1rem'
                  }}
                >
                  <FontAwesomeIcon 
                    icon={getIconFromString(formData.icon)} 
                    size="lg" 
                  />
                </div>
                <div>
                  <h5 className="mb-0">{formData.name || 'Category Name'}</h5>
                  {formData.description && (
                    <div className="small text-muted">{formData.description}</div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            <FontAwesomeIcon icon={faTimes} className="me-2" />
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveCategory}>
            <FontAwesomeIcon icon={faSave} className="me-2" />
            {editCategory ? 'Update Category' : 'Save Category'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CategoryList;
